import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useModuleAccess } from '../../auth/ModuleAccessContext';
import { hamtaHjulLista } from './arshjulApi';

/* Årshjulets panel, global i app-skalet (komponentspec 2).
   Direktlänk: ?arshjul={slug}&datum={kod} fungerar från alla sidor, notiser och mejl.
   Öppen panel skriver parametrarna till URL:en (replace), stängd panel tar bort dem.
   Panelen laddas först när den öppnas, så app-skalet bär ingen extra vikt. */

export const MODULNYCKEL = 'arshjul';
export const AKTUELLT = 'aktuellt';
const ArshjulPanelContext = createContext(null);
const ArshjulPanel = React.lazy(() => import('./components/ArshjulPanel'));

export function ArshjulPanelProvider({ children }) {
  const moduler = useModuleAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const slug = params.get('arshjul');
  const kod = params.get('datum');
  const oppnare = useRef(null);
  const [harOppnats, setHarOppnats] = useState(false);
  // Sant när panelen öppnats inifrån appen (widget, sökträff): tillgången är då känd, så
  // laddningsbilden får visas direkt. En direktlänk väntar på serverns svar (F31).
  const [bekraftad, setBekraftad] = useState(false);
  // En känd modullista utan årshjulet öppnar aldrig panelen. I app-skalet finns ingen
  // modullista (den ligger i Layout), och då avgör serverns svar: 403 stänger panelen
  // innan den visats (ArshjulPanel öppnas först när data finns).
  const tillaten = !moduler || moduler.has(MODULNYCKEL);

  // Bara frågeparametrarna ändras. Sökväg, #flik och state behålls, så att en öppnad
  // panel inte tappar till exempel projektets flik. Refs ger stabila funktioner.
  const plats = useRef({ location, navigate });
  plats.current = { location, navigate };
  const skriv = useCallback((andra) => {
    const { location: loc, navigate: nav } = plats.current;
    const n = new URLSearchParams(loc.search);
    andra(n);
    const q = n.toString();
    nav({ pathname: loc.pathname, search: q ? `?${q}` : '', hash: loc.hash }, { replace: true, state: loc.state });
  }, []);

  const oppna = useCallback((nyttSlug, nyKod = null) => {
    oppnare.current = typeof document !== 'undefined' ? document.activeElement : null;
    setBekraftad(true);
    skriv((n) => {
      n.set('arshjul', nyttSlug);
      if (nyKod) n.set('datum', nyKod); else n.delete('datum');
    });
  }, [skriv]);

  const valjDatum = useCallback((nyKod) => {
    skriv((n) => { if (nyKod) n.set('datum', nyKod); else n.delete('datum'); });
  }, [skriv]);

  const stang = useCallback(() => {
    setBekraftad(false);
    skriv((n) => { n.delete('arshjul'); n.delete('datum'); });
    const el = oppnare.current;
    oppnare.current = null;
    if (el && typeof el.focus === 'function') setTimeout(() => el.focus(), 0);
  }, [skriv]);

  const oppen = Boolean(slug) && slug !== AKTUELLT && tillaten;

  // ?arshjul=aktuellt (sökträffen): öppna det första publicerade hjulet användaren ser.
  useEffect(() => {
    if (slug !== AKTUELLT || !tillaten) return undefined;
    let aktiv = true;
    hamtaHjulLista()
      .then((svar) => {
        if (!aktiv) return;
        const forsta = (svar.hjul || [])[0];
        if (forsta) oppna(forsta.slug, kod); else stang();
      })
      .catch(() => { if (aktiv) stang(); });
    return () => { aktiv = false; };
  }, [slug, kod, tillaten, oppna, stang]);
  useEffect(() => { if (oppen) setHarOppnats(true); }, [oppen]);

  const varde = useMemo(() => ({ oppna, stang, valjDatum, slug: oppen ? slug : null, kod }), [oppna, stang, valjDatum, oppen, slug, kod]);

  return (
    <ArshjulPanelContext.Provider value={varde}>
      {children}
      {harOppnats && (
        <React.Suspense fallback={null}>
          <ArshjulPanel open={oppen} slug={slug} valtKod={kod} visaDirekt={bekraftad} onValj={valjDatum} onClose={stang} />
        </React.Suspense>
      )}
    </ArshjulPanelContext.Provider>
  );
}

/** { oppna(slug, kod?), stang(), valjDatum(kod), slug, kod }. Utan provider: no-op. */
export function useArshjulPanel() {
  return useContext(ArshjulPanelContext) || { oppna: () => {}, stang: () => {}, valjDatum: () => {}, slug: null, kod: null };
}
